import React, { Component } from 'react'

/**
 * Acquisition cohort grid - one row per acquisition-month cohort, one column per calendar month,
 * each cell showing how many of that cohort transacted that month (count + retention %), shaded
 * by retention % like a heatmap. Not an amCharts chart (this isn't a numeric axis plot, it's a
 * matrix table) - a plain HTML table styled to match the rest of the chat's chart cards.
 *
 * Props:
 *   columns - array of column header labels, e.g. ["Apr-26", "May-26", ..., "Sep-26"]
 *             (buildLinkCohortChart in SparcsAIHelper.js builds this as every calendar month from
 *             the earliest cohort through the latest month any cohort actually transacted in)
 *   rows    - array of { cohort_label, cohort_size, cells: [ {count, pct} | null, ... ] }
 *             cells[i] corresponds to columns[i]; null = before that cohort was even acquired
 *             (the blank upper-left triangle every cohort chart has), {count:0, pct:0} = the
 *             cohort existed that month but genuinely nobody transacted.
 *   name    - export/file name (kept for prop-shape consistency with the other chart components;
 *             this component has no export button of its own, so it's otherwise unused)
 *   chartHeight - CSS max-height for the scrollable grid body (defaults to "400px")
 */

// Same light-to-dark interpolation IndiaMapChart.jsx uses for its state heat map, kept as its own
// copy here (teal instead of blue) rather than shared, since this component has no dependency on
// that file and pct here is always a natural 0-100 retention percentage, not a data-driven min/max.
function retentionColor(pct) {
    const ratio = Math.max(0, Math.min(100, pct)) / 100;
    const start = { r: 0xe3, g: 0xf2, b: 0xf1 };
    const end = { r: 0x1f, g: 0x7a, b: 0x7a };
    const r = Math.round(start.r + (end.r - start.r) * ratio);
    const g = Math.round(start.g + (end.g - start.g) * ratio);
    const b = Math.round(start.b + (end.b - start.b) * ratio);
    return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
}

class CohortGridChart extends Component {

    render() {
        const columns = this.props.columns || [];
        const rows = this.props.rows || [];

        if (!rows.length) {
            return <div style={{ fontSize: '12px', color: '#999', padding: '10px' }}>No cohort data for this range.</div>;
        }

        return (
            <div className="chart_div" style={{ overflowX: 'auto', maxHeight: this.props.chartHeight || '400px' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '12px' }}>
                    <thead>
                        <tr>
                            <th style={{ padding: '6px 10px', textAlign: 'left', background: '#f5f5f5', position: 'sticky', left: 0, zIndex: 2 }}>
                                Month of Acquisition
                            </th>
                            <th style={{ padding: '6px 10px', textAlign: 'right', background: '#f5f5f5', position: 'sticky', left: 0, zIndex: 2 }}>
                                Acquired
                            </th>
                            {columns.map((col, i) => (
                                <th key={i} style={{ padding: '6px 10px', textAlign: 'center', background: '#f5f5f5', whiteSpace: 'nowrap' }}>
                                    {col}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, ri) => (
                            <tr key={ri}>
                                <td style={{ padding: '6px 10px', fontWeight: 600, background: '#fff', position: 'sticky', left: 0 }}>
                                    {row.cohort_label}
                                </td>
                                <td style={{ padding: '6px 10px', textAlign: 'right', color: '#c0392b', fontWeight: 600, background: '#fff', position: 'sticky', left: 0 }}>
                                    {row.cohort_size.toLocaleString('en-IN')}
                                </td>
                                {row.cells.map((cell, ci) => {
                                    if (!cell) {
                                        return <td key={ci} style={{ border: '1px solid #eee' }}></td>;
                                    }
                                    return (
                                        <td key={ci} style={{
                                            padding: '6px 10px', textAlign: 'center', border: '1px solid #fff',
                                            background: retentionColor(cell.pct), color: cell.pct > 55 ? '#fff' : '#222'
                                        }}>
                                            <div>{cell.count.toLocaleString('en-IN')}</div>
                                            <div style={{ fontSize: '10px', opacity: 0.85 }}>{cell.pct}%</div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }
}

export default CohortGridChart;
